import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { FormProvider, useForm } from "react-hook-form";
import { Navigate, useParams } from "react-router-dom";
import { z } from "zod";

import { WorkspaceInvitationDisplay } from "./WorkspaceInvitationDisplay";

import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/ui/FormInput";
import { LoadingWithPhrases } from "@/components/ui/LoadingWithPhrases";
import { IS_RECAPTCHA_ENABLED } from "@/constants";
import { useRecaptcha } from "@/providers/GoogleReCaptchaProvider/hooks/useRecaptcha";
import { useCreateAccountFromInvite } from "@/queries/workspace";
import { InviteeOut } from "@/types/workspace-invite";

const CREATE_ACCOUNT_PHRASES = [
  "We're creating your account...",
  "Accepting your invites...",
  "Setting up your workspaces...",
  "Almost there...",
];

const createAccountInviteeSchema = z.object({
  name: z.string().nonempty({ message: "Name is required" }),
  email: z
    .string()
    .email({ message: "Invalid email format" })
    .nonempty({ message: "Email is required" }),
  recaptcha_response: z.string().optional(),
  password: z
    .string()
    .nonempty({ message: "Password is required" })
    .regex(/^(?=\S{6,100}$)(?=.*?\d)(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[^A-Za-z\s0-9])/, {
      message:
        "Password must be at least 6 characters, have 1 digit, 1 lowercase, 1 uppercase, and 1 special character",
    }),
});

export const CreateAccountInviteeContent = ({ inviteeData }: { inviteeData: InviteeOut[] }) => {
  const { invitee } = useParams();
  const [showCaptchaChallenge, setShowCaptchaChallenge] = useState(false);
  const { createAccountFromInvite, isLoading, success } = useCreateAccountFromInvite(verdict => {
    if (verdict === "challenge") {
      setShowCaptchaChallenge(true);
    }
  });
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const { executeRecaptcha } = useRecaptcha();
  const email = inviteeData[0].invitee_email;

  const methods = useForm<z.infer<typeof createAccountInviteeSchema>>({
    mode: "onChange",
    reValidateMode: "onBlur",
    resolver: zodResolver(createAccountInviteeSchema),
    defaultValues: {
      name: "",
      email: email ?? "",
      password: "",
    },
  });

  const handleCaptchaSuccess = (token: string | null) => {
    if (!token) {
      return;
    }

    const data = methods.getValues();

    setShowCaptchaChallenge(false);
    createAccountFromInvite({
      invite_ids: inviteeData.map(inv => inv.id),
      name: data.name,
      email: data.email,
      password: data.password,
      recaptcha_response: token,
    });
  };

  const onSubmit = async (data: z.infer<typeof createAccountInviteeSchema>) => {
    if (!invitee) {
      return;
    }

    if (IS_RECAPTCHA_ENABLED && executeRecaptcha) {
      data.recaptcha_response = await executeRecaptcha("login");
    }

    createAccountFromInvite({
      invite_ids: inviteeData.map(inv => inv.id),
      name: data.name,
      email: data.email,
      password: data.password,
      recaptcha_response: data.recaptcha_response ?? "",
    });
  };

  if (isLoading) {
    return (
      <LoadingWithPhrases
        phrases={CREATE_ACCOUNT_PHRASES}
        description="This may take a few moments while we create your account."
        height="h-[350px]"
        width="w-[350px]"
        titleClassName="text-xl"
        descriptionClassName="text-md"
      />
    );
  }

  if (success) {
    return <Navigate to="/onboarding" />;
  }

  return (
    <>
      <div className="mb-8 flex flex-col gap-3">
        <h1 className="text-3xl font-bold text-gray-900">Welcome to PromptLayer!</h1>
        <WorkspaceInvitationDisplay inviteeData={inviteeData} />
      </div>

      <FormProvider {...methods}>
        <form
          className="flex w-full max-w-md flex-col space-y-4"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <FormInput
            label="Full Name"
            placeholder="Enter your name"
            {...methods.register("name")}
            type="text"
            autoComplete="name"
            error={methods.formState.errors.name?.message ?? ""}
          />
          <FormInput
            label="Email"
            disabled
            value={inviteeData[0]?.invitee_email ?? ""}
            placeholder="Enter your email"
            {...methods.register("email")}
            type="email"
            autoComplete="off-email"
          />
          <FormInput
            label="Password"
            placeholder="Enter your password"
            type="password"
            error={methods.formState.errors.password?.message ?? ""}
            {...methods.register("password")}
            autoComplete="new-password"
          />
          {showCaptchaChallenge && (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={import.meta.env.REACT_APP_RECAPTCHA_V2_SITE_KEY}
              onChange={handleCaptchaSuccess}
            />
          )}
          <div className="flex w-full justify-end pt-4">
            <Button
              type="submit"
              disabled={!methods.formState.isValid || isLoading}
              className="w-fit phone:w-full"
            >
              Create Account
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};
